/**
 * The expressions a sharing form's statements are made of (see
 * `statement.ts`), after SICP §4.1.2. An expression is one of:
 *
 *   variable         a name: the value it is bound to
 *   self-evaluating  a number or `true`/`false`, which stands for itself
 *   application      a procedure name applied to operand expressions, such
 *                    as `truncate(_body, 150, true)`
 *   quote            text that stands for itself, braces and all — what the
 *                    sender typed into a fill-in or a write-in
 *   template         text whose `{…}` tokens are evaluated where the template
 *                    is — a Text slot's text, an optional's text, a select's
 *                    picked option — and joined with its literal runs
 *
 * A template's text is read once into a list of parts — literal runs and
 * `{…}` tokens, each holding the expression its braces read as (see
 * `token.ts`) — and everything else works on the parts: `evaluate` prints
 * them in an environment, `freeNames` lists the names they read, which is
 * what validation and the reshare-story check need, and `issues` lists what
 * is wrong with them before anything is evaluated.
 * Nothing else in the garden form code scans for braces. Classic templates
 * keep their own scanner (`TemplateLogic`) until the cut-over to garden
 * sharing forms (#658).
 */

import {
  EnvironmentLogic,
  type Environment,
  type EvaluatedValue,
  type PrimitiveProcedure,
} from './environment';
import type { Value, ValueType } from './primitive';
import { TOKEN_RE, TokenLogic } from './token';

/** A run of text printed as written. */
export interface LiteralPart {
  readonly kind: 'literal';
  readonly text: string;
}

/**
 * A `{…}` token: its `source` between the braces and the expression it reads
 * as — a name, or a call such as `truncate(_body, 150, true)`.
 */
export interface TokenPart {
  readonly kind: 'token';
  readonly source: string;
  readonly expression: Expression;
}

/** A `{…}` token whose source reads as no expression, and why. */
export interface MalformedPart {
  readonly kind: 'malformed';
  readonly source: string;
  readonly reason: string;
}

export type TemplatePart = LiteralPart | TokenPart | MalformedPart;
/**
 * How a template's evaluated text is spaced. A Text slot keeps its spacing as
 * written for the body's final tidy to settle; an optional's text and a
 * select's option also stand alone as a control's label, so their spacing is
 * tidied as they evaluate. Neither is trimmed: a leading or trailing space in
 * either is what separates it from the slot beside it in the message.
 */
export type TemplateSpacing = 'asWritten' | 'tidied';

export type Expression =
  | { readonly kind: 'variable'; readonly name: string }
  | { readonly kind: 'self-evaluating'; readonly value: number | boolean }
  | {
      readonly kind: 'application';
      readonly operator: string;
      readonly operands: readonly Expression[];
    }
  | { readonly kind: 'quote'; readonly text: string }
  | {
      readonly kind: 'template';
      readonly parts: readonly TemplatePart[];
      readonly spacing: TemplateSpacing;
    };

type Template = Extract<Expression, { kind: 'template' }>;

// The same template text is parsed for every slot, message and keystroke, so
// each parse is kept. Templates are short and few per topic.
const parsed = new Map<string, readonly TemplatePart[]>();
const PARSE_CACHE_LIMIT = 1000;

/** Template text as its literal runs and `{…}` tokens, in order. */
function parse(text: string): readonly TemplatePart[] {
  const known = parsed.get(text);
  if (known !== undefined) return known;
  const parts: TemplatePart[] = [];
  let at = 0;
  const tokens = new RegExp(TOKEN_RE);
  let match: RegExpExecArray | null;
  while ((match = tokens.exec(text)) !== null) {
    const start = match.index;
    if (start > at) {
      parts.push({ kind: 'literal', text: text.slice(at, start) });
    }
    const source = match[1];
    const reading = TokenLogic.read(source);
    parts.push(
      reading.ok
        ? { kind: 'token', source, expression: reading.expression }
        : { kind: 'malformed', source, reason: reading.reason }
    );
    at = start + match[0].length;
  }
  if (at < text.length) parts.push({ kind: 'literal', text: text.slice(at) });
  if (parsed.size >= PARSE_CACHE_LIMIT) parsed.clear();
  parsed.set(text, parts);
  return parts;
}

function template(text: string, spacing: TemplateSpacing): Template {
  return { kind: 'template', parts: parse(text), spacing };
}

function quote(text: string): Expression {
  return { kind: 'quote', text };
}

function variable(name: string): Expression {
  return { kind: 'variable', name };
}

function addVariables(expr: Expression, names: Set<string>): void {
  switch (expr.kind) {
    case 'variable':
      names.add(expr.name);
      return;
    case 'self-evaluating':
    case 'quote':
      return;
    case 'application':
      for (const operand of expr.operands) addVariables(operand, names);
      return;
    case 'template':
      for (const part of expr.parts) {
        if (part.kind === 'token') addVariables(part.expression, names);
      }
      return;
  }
}

/**
 * Every name `expr` reads as a value, each once, in order of first use. The
 * procedures it calls are not among them: those are checked by `issues`.
 */
function freeNames(expr: Expression): readonly string[] {
  const names = new Set<string>();
  addVariables(expr, names);
  return Array.from(names);
}

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth'];

/** The kind of value an expression evaluates to, procedures included. */
type Kind = ValueType | 'procedure';

/** The kind of `value`, as `check` reports it. */
function kindOf(value: EvaluatedValue | undefined): Kind {
  if (EnvironmentLogic.isPrimitiveProcedure(value)) return 'procedure';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'string';
}

/**
 * The kind of value `expr` evaluates to in `env`, with what is wrong with
 * it: a call to a name `env` does not bind to a procedure, or with the wrong
 * number or kind of operands, or a procedure passed as an operand. Each name
 * is looked up in `env`, so a name bound to a procedure there is called as
 * that procedure. A name `env` does not bind reads as text; whether it may
 * be read is the caller's to check.
 */
function check(expr: Expression, problems: string[], env: Environment): Kind {
  switch (expr.kind) {
    case 'variable':
      return kindOf(EnvironmentLogic.lookupVariableValue(env, expr.name));
    case 'self-evaluating':
      return typeof expr.value === 'number' ? 'number' : 'boolean';
    case 'quote':
    case 'template':
      return 'string';
    case 'application': {
      const kinds = expr.operands.map(operand => {
        const kind = check(operand, problems, env);
        if (kind !== 'procedure') return kind;
        problems.push(callItMessage(operand));
        return 'string';
      });
      const operator = EnvironmentLogic.lookupVariableValue(env, expr.operator);
      if (!EnvironmentLogic.isPrimitiveProcedure(operator)) {
        problems.push(`unknown procedure ${expr.operator}`);
        return 'string';
      }
      const { parameters } = operator.procedure;
      if (kinds.length !== parameters.length) {
        problems.push(
          `${expr.operator} takes ${parameters.length} operand${parameters.length === 1 ? '' : 's'}, given ${kinds.length}`
        );
        return 'string';
      }
      kinds.forEach((kind, i) => {
        if (kind !== parameters[i]) {
          problems.push(
            `${expr.operator}'s ${ORDINALS[i] ?? `#${i + 1}`} operand must be a ${parameters[i]}, given a ${kind}`
          );
        }
      });
      return 'string';
    }
  }
}

/** What to say about `expr` evaluating to a procedure where text goes. */
function callItMessage(expr: Expression): string {
  const name = expr.kind === 'variable' ? expr.name : 'it';
  return `${name} is a procedure; call it as ${name}(…)`;
}

/**
 * The kind of value `expr` evaluates to in `env` (see `check`), and what is
 * wrong with it whatever value it is: a define can bind any kind of value,
 * a procedure included.
 */
function analyze(
  expr: Expression,
  env: Environment = EnvironmentLogic.baseEnvironment
): { readonly kind: Kind; readonly problems: readonly string[] } {
  const problems: string[] = [];
  const kind = check(expr, problems, env);
  return { kind, problems };
}

/**
 * What is wrong with `expr`'s tokens, one message per token, checked in
 * `env` (the base environment unless given): a source that reads as no
 * expression, a call that cannot be applied, or a token whose value is not
 * text. Empty for an expression that can be printed wherever its names are
 * bound as they are in `env`.
 */
function issues(
  expr: Expression,
  env: Environment = EnvironmentLogic.baseEnvironment
): readonly string[] {
  if (expr.kind !== 'template') return [];
  const found: string[] = [];
  for (const part of expr.parts) {
    if (part.kind === 'malformed') {
      found.push(`{${part.source}} cannot be read: ${part.reason}`);
      continue;
    }
    if (part.kind !== 'token') continue;
    const { kind, problems } = analyze(part.expression, env);
    const all = [...problems];
    if (kind === 'procedure') all.push(callItMessage(part.expression));
    else if (kind !== 'string') all.push(`it prints a ${kind}, not text`);
    for (const problem of all) found.push(`{${part.source}}: ${problem}`);
  }
  return found;
}

/**
 * SICP's `apply-primitive-procedure`: its operands checked against its
 * parameters, then its implementation applied to them. Validation catches a
 * bad call before it gets here; this throws on one rather than print a
 * wrong message.
 */
function applyPrimitiveProcedure(
  { procedure }: PrimitiveProcedure,
  operands: readonly EvaluatedValue[]
): string {
  const { name, parameters } = procedure;
  if (
    operands.length !== parameters.length ||
    operands.some((operand, i) => typeof operand !== parameters[i])
  ) {
    throw new Error(`Cannot apply ${name} to its operands`);
  }
  return procedure.implementation(operands as readonly Value[]);
}

/**
 * SICP's `apply`: what `operator` evaluated to, applied to `operands`. Only
 * primitive procedures exist, so anything else is not a procedure.
 */
function apply(
  operatorName: string,
  operator: EvaluatedValue,
  operands: readonly EvaluatedValue[]
): string {
  if (!EnvironmentLogic.isPrimitiveProcedure(operator)) {
    throw new Error(`${operatorName} is not a procedure`);
  }
  return applyPrimitiveProcedure(operator, operands);
}

/** How `evalIn` looks a name up: SICP's `lookup-variable-value`, curried. */
type Lookup = (name: string) => EvaluatedValue | undefined;

/**
 * SICP's `eval`, with the environment given as the way it looks names up.
 * Undefined when the expression reads a name nothing binds, outside a
 * template's tokens, which throw on one.
 */
function evalIn(expr: Expression, lookup: Lookup): EvaluatedValue | undefined {
  switch (expr.kind) {
    case 'variable':
      return lookup(expr.name);
    case 'self-evaluating':
      return expr.value;
    case 'quote':
      return expr.text;
    case 'template':
      return printTemplate(expr, lookup);
    case 'application': {
      const operator = lookup(expr.operator);
      if (operator === undefined) return undefined;
      const operands: EvaluatedValue[] = [];
      for (const operand of expr.operands) {
        const value = evalIn(operand, lookup);
        if (value === undefined) return undefined;
        operands.push(value);
      }
      return apply(expr.operator, operator, operands);
    }
  }
}

/** A value as the text it prints. Only text prints; anything else throws. */
function printed(value: EvaluatedValue): string {
  if (EnvironmentLogic.isPrimitiveProcedure(value)) {
    const { name } = value.procedure;
    throw new Error(`${name} is a procedure; call it as ${name}(…)`);
  }
  if (typeof value !== 'string') {
    throw new Error(`${String(value)} is a ${typeof value}, not text`);
  }
  return value;
}

/**
 * A template printed. Text slots, optional text and select options all print
 * by this one rule: a token prints its value in place, and the spaces around
 * it are left for the spacing to settle. A name nothing binds, a malformed
 * token or a call that cannot be applied throws: validation rejects each
 * before it gets here, and literal braces in a sent message are worse than a
 * loud failure.
 */
function printTemplate({ parts, spacing }: Template, lookup: Lookup): string {
  let text = '';
  for (const part of parts) {
    if (part.kind === 'literal') {
      text += part.text;
      continue;
    }
    if (part.kind === 'malformed') {
      throw new Error(`Malformed token {${part.source}}`);
    }
    const value = evalIn(part.expression, lookup);
    if (value === undefined) throw new Error(`Unknown token {${part.source}}`);
    text += printed(value);
  }
  return spacing === 'asWritten' ? text : tidyText(text);
}

/**
 * SICP's `(eval exp env)`: what `expr` evaluates to in `env`, text, a number,
 * a flag or a procedure. A name nothing binds reads as blank, except in a
 * template's tokens, which throw on one.
 */
function evaluate(expr: Expression, env: Environment): EvaluatedValue {
  return (
    evalIn(expr, name => EnvironmentLogic.lookupVariableValue(env, name)) ?? ''
  );
}

/**
 * What `expr` prints in `env`: its value (see `evaluate`), which must be
 * text. Anything else throws; validation rejects such an expression before
 * it gets here.
 */
function print(expr: Expression, env: Environment): string {
  return printed(evaluate(expr, env));
}

// GARDEN-CUTOVER(#658): a classic template resolves its names from a map
// rather than an environment, so its tokens evaluate with that map standing
// in for the frames above the base environment.
/**
 * What the token whose braces hold `source` prints, reading its names with
 * `resolve` and anything `resolve` does not bind from the base
 * environment, where the primitive procedures are. Null when the source
 * reads a name neither binds.
 */
function evaluateSource(
  source: string,
  resolve: (name: string) => string | null
): string | null {
  const reading = TokenLogic.read(source);
  if (!reading.ok) throw new Error(`Malformed token {${source}}`);
  const value = evalIn(
    reading.expression,
    name =>
      resolve(name) ??
      EnvironmentLogic.lookupVariableValue(
        EnvironmentLogic.baseEnvironment,
        name
      )
  );
  return value === undefined ? null : printed(value);
}
// END GARDEN-CUTOVER(#658)

/**
 * The spacing within a line that a message body's tidy settles, without
 * trimming, for text shown on its own: a run of spaces and tabs after a word
 * becomes one space, and none is left before punctuation. A line's indent is
 * kept, so an indented paragraph stays indented. So a token that evaluates to
 * nothing leaves no gap — "Hi {_recipientInformalName}, welcome" reads "Hi,
 * welcome" — and a sender's own "wow !" reads "wow!".
 */
function tidyText(text: string): string {
  return text
    .replace(/[ \t]+([,.;:!?])/g, '$1')
    .replace(/(\S)[ \t]{2,}/g, '$1 ');
}

export const ExpressionLogic = {
  template,
  quote,
  variable,
  freeNames,
  analyze,
  issues,
  tidyText,
  evaluate,
  print,
  evaluateSource,
};
